import { forwardRef } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import classNames from 'styles/utils/classNames';

type Props = InputHTMLAttributes<HTMLInputElement> & {
  /** Optional adornment rendered at the start of the field, e.g. a "$". */
  prefix?: string;
  /** Optional adornment rendered at the end of the field, e.g. a calendar icon. */
  suffix?: ReactNode;
};

/** The design's standard 42px filled input. */
const FormTextInput = forwardRef<HTMLInputElement, Props>(function FormTextInput(
  { prefix, suffix, className, ...props },
  ref,
) {
  return (
    <div className="flex h-[2.625rem] items-center gap-ds-xs rounded-lg bg-color-bg-input-lightmode-primary px-ds-md dark:bg-color-bg-input-darkmode-primary">
      {prefix && (
        <span className="typography-product-body text-color-text-lightmode-placeholder dark:text-color-text-darkmode-placeholder">
          {prefix}
        </span>
      )}
      <input
        ref={ref}
        className={classNames(
          'input-unstyled typography-product-body w-full border-none bg-transparent p-0 text-color-text-lightmode-primary outline-none ring-transparent placeholder:text-color-text-lightmode-placeholder focus:outline-none dark:text-color-text-darkmode-primary',
          // Date/time inputs: the design shows its own suffix icon, so stretch the
          // native webkit picker trigger invisibly across the field instead.
          'relative [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0',
          className,
        )}
        {...props}
      />
      {suffix && (
        <span className="pointer-events-none shrink-0 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon">
          {suffix}
        </span>
      )}
    </div>
  );
});

export default FormTextInput;
